/**
 * EditMealServingsWrapper - Translation layer between new hooks architecture and legacy EditMealServingsModal
 *
 * This component:
 * 1. Consumes data from the new context providers
 * 2. Translates it into the format expected by EditMealServingsModal
 * 3. Implements onChangeParticipants business logic for leftovers calculations
 * 4. Manages modal state through modalManager for dual-modal support
 * 5. Handles conflict resolution with ConfirmOverwriteModal
 * 
 * Design Decision: totalDays Implementation
 * ========================================
 * We handle totalDays (meal duration) functionally via the onChangeParticipants callback
 * rather than storing it as component state. This is cleaner than the legacy Editor
 * approach which used this.setState({totalDaysSet}) and complex state synchronization.
 * 
 * EditMealServingsModal calculates totalDays internally and passes it directly to 
 * onChangeParticipants, eliminating the need for cross-component state management.
 */

import React, { useCallback, useMemo, Component } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import EditMealServingsModalPrimary from '@components/Recipes/EditMealServingsModalPrimary.react';
import { useMealPlanData, useAssets, useMealOperations, useUIState, usePermissions } from '@components/Plans/Editor/contexts';
import UserStore from '@stores/UserStore';
import { getMealAndLeftovers, reuseOrCreateLeftovers, removeUnusedLeftovers, findPrimaryMeal, getOtherFamilyMeals } from '@utils/proMeals';
import { recalculateScaling, getParticipantsForSpecificMeal } from '@utils/Meals';

/**
 * Generate a short 8-character UUID (consistent with backend format)
 * @returns {string} 8-character hexadecimal UUID
 */
const generateShortUuid = () => {
    return uuid().substring(0, 8);
};

// Legacy context provider for EditMealServingsModal
class LegacyContextProvider extends Component {
    static propTypes = {
        legacyContext: PropTypes.object.isRequired,
        onChangeParticipants: PropTypes.func.isRequired,
        children: PropTypes.node.isRequired,
    };

    static childContextTypes = {
        isPro: PropTypes.bool,
        user: PropTypes.object,
        meals: PropTypes.array,
    };

    getChildContext() {
        const { legacyContext } = this.props;

        return {
            isPro: legacyContext.isPro,
            user: legacyContext.user,
            meals: legacyContext.meals,
        };
    }

    render() {
        return this.props.children;
    }
}

const EditMealServingsWrapper = ({ modalManager, addDay, applyOverlapResolution }) => {
    // Get data from new context providers
    const { profile, plan, maxOffset } = useMealPlanData();
    const { recipes, foods, details } = useAssets();
    const mealOperations = useMealOperations();
    const { isPro, editableParticipants } = usePermissions();
    const { isMobile } = useUIState();

    // Access meals through plan.items (the correct data source)
    const allMeals = plan?.items || [];


    // Get modal context and determine if modal should be open
    const isModalOpen = modalManager.openModals?.editMealServings;
    const modalContext = modalManager.getModalData('editMealServings');
    
    if (!isModalOpen) {
        return null;
    }

    // Extract the meal being edited from context
    const { meal: targetMeal, pendingLeftovers } = modalContext;

    // Find all meals in the same slot as the target meal
    const slotMeals = allMeals.filter(meal =>
        meal.offset === targetMeal.offset &&
        meal.meal === targetMeal.meal &&
        !meal.deleted
    );

    // Get participants for the specific meal being edited (not including leftovers)
    const participants = getParticipantsForSpecificMeal(targetMeal, profile) || [];

    // Build contents object (recipes/foods indexed by UUID)
    const contents = useMemo(() => {
        const allContents = { ...recipes, ...foods };

        // Add details to contents if available
        slotMeals.forEach(meal => {
            if (meal.recipe_uuid && recipes[meal.recipe_uuid]) {
                allContents[meal.recipe_uuid] = {
                    ...recipes[meal.recipe_uuid],
                    details: meal.details_uuid ? details[meal.details_uuid] : null
                };
            }
            if (meal.food_uuid && foods[meal.food_uuid]) {
                allContents[meal.food_uuid] = foods[meal.food_uuid];
            }
        });

        return allContents;
    }, [recipes, foods, details, slotMeals]);

    // Get the primary content (recipe or food) for the target meal
    const primaryContent = useMemo(() => {
        if (targetMeal.recipe_uuid && recipes[targetMeal.recipe_uuid]) {
            return recipes[targetMeal.recipe_uuid];
        }
        if (targetMeal.food_uuid && foods[targetMeal.food_uuid]) {
            return foods[targetMeal.food_uuid];
        }
        return null;
    }, [targetMeal, recipes, foods]);

    // Calculate current scaling
    const currentScaling = targetMeal.scaling || 1;

    /**
     * Apply the leftover creation changes using batch operations
     * Extracted as separate function to be called both when no conflicts exist
     * and after conflict resolution via ConfirmOverwriteModal
     */
    const applyLeftoverChanges = useCallback((updatedMeal, mealsToAdd, mealsToRemove, conflictingMeals = []) => {
        // Prepare data for applyOverlapResolution format
        // updatedMeal can be a single meal or an array of meals (for multi-dish)
        const updatedMeals = Array.isArray(updatedMeal) ? updatedMeal : [updatedMeal];
        const dirtyMeals = [...updatedMeals];
        const mealsToDelete = [
            // Remove conflicting meals
            ...conflictingMeals.flatMap(conflict => conflict.existingMeals),
            // Remove excess leftover meals
            ...mealsToRemove
        ];

        // Add new/updated leftover meals to dirtyMeals
        mealsToAdd.forEach(meal => {
            // Check if this is an update to an existing leftover
            const existingMeal = allMeals.find(m => 
                m.id === meal.id || 
                (m.offset === meal.offset && 
                 m.meal === meal.meal && 
                 m.meal_type === 'leftover' && 
                 m.parent === meal.parent &&
                 !m.deleted)
            );
            
            if (existingMeal) {
                // For existing meals, add to delete list and include updated version in dirtyMeals
                mealsToDelete.push(existingMeal);
            }
            
            // Add the new/updated meal
            dirtyMeals.push(meal);
        });

        // Use the shared applyOverlapResolution function
        const result = applyOverlapResolution(dirtyMeals, mealsToDelete, []);
        
        // Close the modal
        modalManager.closeModal('editMealServings');
        
        return result;
    }, [targetMeal, allMeals, applyOverlapResolution, modalManager]);

    /**
     * Handle participant changes - core business logic
     * This is where we implement leftovers calculations and conflict resolution
     *
     * @param {Array} newParticipants - Updated participant list with portions
     * @param {Object} newScaling - Recipe scaling factors
     * @param {Array} leftoverOffsets - Custom leftover day offsets (optional)
     * @param {Array} replaceMeals - Meals to replace (from conflict resolution)
     * @param {boolean} editLeftovers - Whether to open leftover modal
     * @param {boolean} returnLeftoversOnly - Whether to return leftovers only
     * @param {number} extraServings - Extra servings to store
     * @param {Array} dishes - Updated dishes array with logged_amount changes
     */
    const onChangeParticipants = useCallback((newParticipants, newScaling, leftoverOffsets, replaceMeals, editLeftovers, returnLeftoversOnly = false, extraServings = 0, dishes = null) => {

        // Filter out leftovers with no participants - they should be deleted
        if (leftoverOffsets && Array.isArray(leftoverOffsets)) {
            leftoverOffsets = leftoverOffsets.filter(lo =>
                lo.participants && lo.participants.length > 0
            );
        }

        // Recalculate scaling for entire meal family when editing
        let familyScaling = newScaling;

        // Get the recipe/food for the meal being edited
        const recipe = targetMeal.recipe_uuid ? recipes[targetMeal.recipe_uuid] : null;
        const food = targetMeal.food_uuid ? foods[targetMeal.food_uuid] : null;
        const content = recipe || food;

        // Initialize leftoversList outside of if(content) block for dish scaling calculations
        let leftoversList = [];

        if (content) {
            // Get all meals in the family (main + leftovers)
            // If editing a leftover, find the parent main meal first
            const primaryMeal = findPrimaryMeal(targetMeal, allMeals);
            const mealFamily = primaryMeal ? getMealAndLeftovers(primaryMeal, { items: allMeals }) : [targetMeal];

            // Determine which leftovers to use for scaling calculation

            if (leftoverOffsets !== null && leftoverOffsets !== undefined && Array.isArray(leftoverOffsets)) {
                // Use pending leftovers from modal (user's current selection, could be empty array for removal)
                leftoversList = leftoverOffsets.map(lo => ({
                    uuid: null, // Pending, no uuid yet
                    leftoverOffset: lo.leftoverOffset || lo.offset,
                    mealType: lo.mealType,
                    participants: lo.participants || []
                }));
            } else {
                // Not editing leftovers - use existing leftovers from database
                const existingLeftovers = mealFamily.slice(1); // Exclude main meal
                const leftoverBeingEdited = targetMeal.meal_type === 'leftover' ? targetMeal : null;

                // Map all existing leftovers, using NEW participants for the one being edited
                leftoversList = existingLeftovers.map(leftover => {
                    const isBeingEdited = leftoverBeingEdited && (
                        (leftover.id && leftoverBeingEdited.id && leftover.id === leftoverBeingEdited.id) ||
                        (leftover.uuid && leftoverBeingEdited.uuid && leftover.uuid === leftoverBeingEdited.uuid)
                    );

                    return {
                        uuid: leftover.uuid || leftover.id,
                        leftoverOffset: leftover.offset,
                        mealType: leftover.meal,
                        // Use NEW participants if this is the leftover being edited, otherwise use existing
                        participants: isBeingEdited ? newParticipants : (getParticipantsForSpecificMeal(leftover, profile) || [])
                    };
                });
            }

            // Recalculate scaling for entire family
            familyScaling = recalculateScaling(
                targetMeal,
                newParticipants,
                content,
                leftoversList,
                allMeals,
                profile,
                extraServings
            );
        }

        // Helper to calculate scaling for each dish in combo meals
        // - targetMeal: uses pre-calculated familyScaling (considers leftovers)
        // - other dishes: calculate independently based on their recipe.servings
        const getDishScaling = (dish) => {
            const dishKey = dish.uuid || dish.id;

            // Trust the modal's scalingMap as source of truth (mirrors consumer side).
            // The modal computed scaling using the updated logged_amount, so this is
            // always correct — avoids using stale logged_amount from the plan state.
            if (typeof newScaling === 'object' && newScaling !== null && newScaling[dishKey] !== undefined) {
                return newScaling[dishKey];
            }

            const isTarget = (dish.id && dish.id === targetMeal.id) ||
                             (dish.uuid && dish.uuid === targetMeal.uuid);
            if (isTarget) {
                return familyScaling;
            }
            const dishContent = dish.recipe_uuid ? recipes[dish.recipe_uuid] : foods[dish.food_uuid];
            return dishContent
                ? recalculateScaling(dish, newParticipants, dishContent, leftoversList, allMeals, profile)
                : familyScaling;
        };

        // V2 Enhancement: Handle leftover workflow like consumer V2
        if (editLeftovers) {
            // User clicked "Add Leftovers" - open leftover modal for selection

            // Calculate accurate leftover servings if leftovers already exist
            let totalLeftoverServings = 0;
            if (targetMeal?.leftover_offsets && targetMeal.leftover_offsets.length > 0) {
                // Sum actual participants for each existing leftover
                const allMealsInFamily = getMealAndLeftovers(targetMeal, plan.items);
                allMealsInFamily.slice(1).forEach(leftoverMeal => {  // Skip first (main meal)
                    const leftoverParticipants = getParticipantsForSpecificMeal(leftoverMeal, profile);
                    totalLeftoverServings += leftoverParticipants.reduce((sum, p) => sum + p.portion, 0);
                });
            }

            const leftoverParams = {
                totalLeftoverServings,
                leftoverOffset: targetMeal.offset + 1,
                meal: targetMeal,
                participants: newParticipants,
                leftovers: leftoverOffsets,
            };

            modalManager.openLeftoverOffsetsModal({
                ...leftoverParams,
                returnMode: returnLeftoversOnly
            });
            return;
        }

        // Handle case where leftoverOffsets are provided from the modal selection
        // This includes both when leftovers are selected AND when all are removed (empty array)
        if (leftoverOffsets !== null && leftoverOffsets !== undefined) {
            // This mimics consumer's onSelectLeftoverDates → onChangeParticipants flow
            // For multi-dish meals, we need to update ALL dishes in the slot
            const updatedSlotMeals = slotMeals.map(slotMeal => {
                let updatedMeal = {
                    ...slotMeal,
                    participants: newParticipants.map(p => p.uuid),
                    scaling: getDishScaling(slotMeal),
                    extra_servings: extraServings,
                };

                // Apply logged_amount changes from dishes if provided (from EditMealServingsModal)
                if (dishes && dishes.length > 0) {
                    const matchingDish = dishes.find(dishWrapper => {
                        const dishMain = dishWrapper.main || dishWrapper;
                        // Match by id or uuid ONLY (not recipe_uuid, as it's not unique for multi-dish with same recipe)
                        return (dishMain.id && dishMain.id === slotMeal.id) ||
                               (dishMain.uuid && dishMain.uuid === slotMeal.uuid);
                    });

                    if (matchingDish) {
                        const dishMain = matchingDish.main || matchingDish;
                        if (dishMain.logged_amount !== undefined) {
                            updatedMeal.logged_amount = dishMain.logged_amount;
                            updatedMeal.logged_unit = dishMain.logged_unit || 'serving';
                        }
                    }
                }

                return updatedMeal;
            });

            // Consumer pattern: Reuse existing leftovers to prevent duplicates and preserve IDs
            // For combo meals, handle leftovers for ALL dishes in the slot using each dish's scaling
            const { leftoverMeals, reusedLeftoverIds } = reuseOrCreateLeftovers(updatedSlotMeals, leftoverOffsets, allMeals);

            // Remove existing leftovers that were NOT reused (consumer pattern)
            const mealsToRemove = removeUnusedLeftovers(updatedSlotMeals, reusedLeftoverIds, allMeals);

            // Include other family meals (not in updatedSlotMeals or leftoverMeals) that need scaling update
            // Skip this in returnMode (leftoverOffsets present) because we're doing complete leftover replacement
            const otherFamilyMeals = (content && !leftoverOffsets)
                ? getOtherFamilyMeals(targetMeal, updatedSlotMeals, leftoverMeals, mealsToRemove, allMeals, familyScaling)
                : [];

            // Check if any leftover offsets exceed current plan days and extend if needed
            if (leftoverMeals.length > 0) {
                const maxLeftoverOffset = Math.max(...leftoverMeals.map(leftoverMeal => leftoverMeal.offset));
                const currentMaxOffset = maxOffset;
                
                // Add days sequentially to ensure state updates properly
                for (let targetOffset = currentMaxOffset + 1; targetOffset <= maxLeftoverOffset; targetOffset++) {
                    const success = addDay();
                    if (!success) {
                        break; // Hit the max limit (7 days max)
                    }
                }
            }

            // Apply both the updated meals, leftover meals, and other family meals
            const dirtyMeals = [...updatedSlotMeals, ...leftoverMeals, ...otherFamilyMeals];
            applyOverlapResolution(dirtyMeals, [...(replaceMeals || []), ...mealsToRemove], []);
            modalManager.closeModal('editMealServings');
            return;
        }

        // Step 1: Update ALL meals in the slot with new participants and scaling
        // For multi-dish meals, each dish gets its own scaling based on its recipe.servings
        const updatedSlotMealsMain = slotMeals.map(slotMeal => {
            let updatedMeal = {
                ...slotMeal,
                participants: newParticipants.map(p => p.uuid),
                scaling: getDishScaling(slotMeal),
                extra_servings: extraServings,
            };

            // Apply logged_amount changes from dishes if provided
            if (dishes && dishes.length > 0) {
                const matchingDish = dishes.find(dishWrapper => {
                    const dishMain = dishWrapper.main || dishWrapper;
                    // Match by id or uuid ONLY (not recipe_uuid, as it's not unique for multi-dish with same recipe)
                    return (dishMain.id && dishMain.id === slotMeal.id) ||
                           (dishMain.uuid && dishMain.uuid === slotMeal.uuid);
                });

                if (matchingDish) {
                    const dishMain = matchingDish.main || matchingDish;
                    if (dishMain.logged_amount !== undefined) {
                        updatedMeal.logged_amount = dishMain.logged_amount;
                        updatedMeal.logged_unit = dishMain.logged_unit || 'serving';
                    }
                }
            }

            return updatedMeal;
        });

        // For backwards compatibility, keep updatedMeal as the targetMeal
        const updatedMeal = updatedSlotMealsMain.find(m => m.id === targetMeal.id || m.uuid === targetMeal.uuid) || updatedSlotMealsMain[0];

        // Step 2: Calculate leftovers and handle removal of excess leftovers
        const mealsToAdd = [];
        const mealsToRemove = [];
        
        // Handle meals that should be replaced due to conflict resolution
        if (replaceMeals && replaceMeals.length > 0) {
            mealsToRemove.push(...replaceMeals);
        }
        
        // First, find all existing leftovers for this meal
        const existingLeftovers = allMeals.filter(meal =>
            meal.meal_type === 'leftover' &&
            meal.parent === targetMeal.id &&
            !meal.deleted
        );

        // IMPORTANT: Only create leftovers from fresh/regular meals, not from leftover meals
        // (leftovers cannot have leftovers - matches consumer flow behavior)
        if (leftoverOffsets && leftoverOffsets.length > 0 && targetMeal.meal_type !== 'leftover') {
            // Find the maximum offset we'll need for plan extension
            const maxLeftoverOffset = Math.max(...leftoverOffsets.map(lo => lo.leftoverOffset));

            // Automatically extend plan if leftovers go beyond current maxOffset
            // Note: addDay() returns true on success, false if already at maximum
            let currentMaxOffset = maxOffset;
            while (maxLeftoverOffset > currentMaxOffset && currentMaxOffset < 6) {
                const extensionSucceeded = addDay();
                if (extensionSucceeded) {
                    currentMaxOffset++;
                } else {
                    break;
                }
            }

            // Generate leftover meals for each requested offset
            leftoverOffsets.forEach(leftoverSpec => {
                const leftoverOffset = leftoverSpec.leftoverOffset;

                // Check if a leftover already exists for this day
                const existingLeftover = allMeals.find(meal =>
                    meal.offset === leftoverOffset &&
                    meal.meal === targetMeal.meal &&
                    meal.meal_type === 'leftover' &&
                    meal.parent === targetMeal.id &&
                    !meal.deleted
                );

                // Only create leftover if it doesn't already exist
                if (!existingLeftover) {
                    // Create leftovers (plan has been extended if needed)
                    // Note: Still check against a reasonable max (7 days = offset 6)
                    if (leftoverOffset <= 6) {
                        const leftoverMeal = {
                            ...targetMeal,
                            id: generateShortUuid(), // Generate proper 8-char UUID
                            meal_type: 'leftover',
                            parent: targetMeal.id,
                            offset: leftoverOffset,
                            participants: newParticipants.map(p => p.uuid),
                            scaling: familyScaling,
                            // Clear recipe-specific fields for leftovers
                            recipe_title: `${targetMeal.recipe_title} (Leftovers)`,
                        };

                        mealsToAdd.push(leftoverMeal);
                    }
                } else {
                    
                    // Update existing leftover's participants and scaling
                    const updatedLeftover = {
                        ...existingLeftover,
                        participants: newParticipants.map(p => p.uuid),
                        scaling: familyScaling
                    };
                    
                    mealsToAdd.push(updatedLeftover);
                }
            });
        }

        // Step 2.5: Handle removal of excess leftovers
        // Remove leftovers that are NOT in the requested leftoverOffsets array
        if (leftoverOffsets?.length > 0) {
            const requestedOffsets = new Set(leftoverOffsets.map(lo => lo.leftoverOffset));
            existingLeftovers.forEach(leftover => {
                // If this leftover's offset is not in the requested set, mark it for removal
                if (!requestedOffsets.has(leftover.offset)) {
                    mealsToRemove.push(leftover);
                }
            });
        } else {
            // No leftovers requested - remove all existing leftovers
            mealsToRemove.push(...existingLeftovers);
        }

        // Step 3: Check for conflicts with existing meals
        // Group by slot (offset + mealType) to handle multi-dish meals
        const conflictsBySlot = {};
        mealsToAdd.forEach(leftoverMeal => {
            const existingMeals = allMeals.filter(meal =>
                meal.offset === leftoverMeal.offset &&
                meal.meal === leftoverMeal.meal &&
                meal.id !== leftoverMeal.id &&
                !meal.deleted
            );

            if (existingMeals.length > 0) {
                const slotKey = `${leftoverMeal.offset}-${leftoverMeal.meal}`;
                if (!conflictsBySlot[slotKey]) {
                    conflictsBySlot[slotKey] = {
                        offset: leftoverMeal.offset,
                        mealType: leftoverMeal.meal,
                        leftoverMeals: [],
                        existingMeals: existingMeals
                    };
                }
                conflictsBySlot[slotKey].leftoverMeals.push(leftoverMeal);
            }
        });

        // Step 4: Handle conflicts or proceed with updates
        const conflictingSlots = Object.values(conflictsBySlot);
        if (conflictingSlots.length > 0) {
            // Create overlap objects for ConfirmOverwriteModal
            // Each overlap represents one slot with ALL dishes' leftover meals
            const overlaps = conflictingSlots.map((conflict, index) => ({
                mealType: conflict.mealType,
                offset: conflict.offset,
                destMeals: conflict.existingMeals, // Existing meals being overwritten
                sourceMeals: conflict.leftoverMeals, // ALL leftover meals for this slot (all dishes)
                originalIndex: index
            }));

            // Open ConfirmOverwriteModal directly when conflicts are detected
            modalManager.openOverlapModal({
                overlaps,
                mealsToReschedule: mealsToAdd, // Leftover meals to add
                clears: [],
                targetOffset: targetMeal.offset,
                targetMealType: targetMeal.meal,
                isRepeatMeal: true, // Use repeat mode for simpler leftover handling
                applyOverlapResolution: (dirtyMeals, mealsToDelete, leftoverOffsets) => {
                    // The ConfirmOverwriteModal has resolved the conflicts, now we need to:
                    // 1. Apply the leftover changes (original meal update + leftover cleanup)
                    // 2. Close the edit servings modal
                    // Include other family meals that need scaling update
                    const otherFamilyMealsConflict = content
                        ? getOtherFamilyMeals(targetMeal, updatedSlotMealsMain, mealsToAdd, [], allMeals, familyScaling)
                        : [];
                    // Pass all updated slot meals + other family meals for multi-dish support
                    applyLeftoverChanges([...updatedSlotMealsMain, ...otherFamilyMealsConflict], mealsToAdd, mealsToRemove, conflictingSlots);

                    // Return success to let ConfirmOverwriteModal know the operation completed
                    return { success: true };
                }
            });

            return;
        }

        // Include other family meals (not in updatedSlotMealsMain or mealsToAdd) that need scaling update
        const otherFamilyMealsMain = content
            ? getOtherFamilyMeals(targetMeal, updatedSlotMealsMain, mealsToAdd, [], allMeals, familyScaling)
            : [];

        // Step 5: Apply all changes using the extracted function (when no conflicts)
        // Pass all updated slot meals, not just the target meal (for multi-dish support)
        // Include other family meals in updatedSlotMealsMain
        applyLeftoverChanges([...updatedSlotMealsMain, ...otherFamilyMealsMain], mealsToAdd, mealsToRemove);
    }, [targetMeal, allMeals, plan.duration, mealOperations, modalManager, addDay, maxOffset, applyLeftoverChanges]);

    // Build legacy context object that EditMealServingsModal expects
    const legacyContext = useMemo(() => {
        return {
            isPro: isPro !== false, // Default to true if not specified
            user: profile, // Use the patient's profile, not the logged-in user
            meals: slotMeals, // Just the meals in this slot
        };
    }, [isPro, slotMeals, profile]);

    // Render the legacy modal with context provider
    return (
        <LegacyContextProvider
            legacyContext={legacyContext}
            onChangeParticipants={onChangeParticipants}
        >
            <EditMealServingsModalPrimary
                mealDishes={slotMeals}
                contents={contents}
                profile={profile}
                plan={{ items: allMeals }}
                participants={participants}
                editableParticipants={editableParticipants !== false}
                onChangeParticipants={onChangeParticipants}
                closeModal={() => modalManager.closeModal('editMealServings')}
                returnLeftoversOnly={true}
                pendingLeftovers={pendingLeftovers}
            />
        </LegacyContextProvider>
    );
};

EditMealServingsWrapper.propTypes = {
    modalManager: PropTypes.shape({
        openModals: PropTypes.object.isRequired,
        getModalData: PropTypes.func.isRequired,
        closeModal: PropTypes.func.isRequired,
    }).isRequired,
    addDay: PropTypes.func.isRequired,
    applyOverlapResolution: PropTypes.func.isRequired,
};

export default EditMealServingsWrapper;
